import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { CloseIcon } from './icons'
import { ChatMessage } from './ChatMessage'
import { ChatComposer } from './ChatComposer'
import { TypingIndicator } from './TypingIndicator'
import type {
  Operator,
  PresenceStatus,
  ViewMessage,
  WebChatLabels,
} from './types'

type ChatPanelProps = {
  panelId: string
  operator: Operator
  status: PresenceStatus
  statusLabel: string // e.g. "Online · Consultant dulapuri"
  messages: ViewMessage[]
  labels: WebChatLabels
  isTyping?: boolean
  greeting?: string
  typingLabel: string
  composerDisabled?: boolean
  notice?: string // shown in place of the composer (closed / error)
  aboveComposer?: React.ReactNode // e.g. contact-capture bar
  onClose: () => void
  onSend: (text: string) => void
}

const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')

const NEAR_BOTTOM_PX = 80

export const ChatPanel: React.FC<ChatPanelProps> = ({
  panelId,
  operator,
  status,
  statusLabel,
  messages,
  labels,
  isTyping = false,
  greeting,
  typingLabel,
  composerDisabled = false,
  notice,
  aboveComposer,
  onClose,
  onSend,
}) => {
  const logRef = useRef<HTMLDivElement>(null)
  const nearBottomRef = useRef(true)

  const trackScroll = () => {
    const el = logRef.current
    if (!el) return
    nearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX
  }

  // Auto-scroll to bottom on new messages ONLY when already near the bottom.
  useLayoutEffect(() => {
    const el = logRef.current
    if (el && nearBottomRef.current) el.scrollTop = el.scrollHeight
  }, [messages, isTyping])

  // Escape closes (non-modal: no focus trap, no scroll lock).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const empty = messages.length === 0 && !isTyping

  return (
    <div
      id={panelId}
      className="wc-panel"
      role="dialog"
      aria-modal="false"
      aria-label={operator.name}
    >
      <header className="wc-header">
        <span className="wc-avatar" aria-hidden="true">
          {operator.avatarUrl ? (
            <img src={operator.avatarUrl} alt="" />
          ) : (
            initials(operator.name)
          )}
        </span>
        <div className="wc-head-meta">
          <p className="wc-name">{operator.name}</p>
          <p className="wc-status">
            <span className={`wc-dot wc-dot--${status}`} aria-hidden="true" />
            {statusLabel}
          </p>
        </div>
        <button
          type="button"
          className="wc-close"
          aria-label={labels.close}
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </header>

      <div
        className="wc-log"
        role="log"
        aria-live="polite"
        ref={logRef}
        onScroll={trackScroll}
      >
        {empty && greeting ? (
          <p className="wc-greeting">{greeting}</p>
        ) : (
          <>
            {messages.map((m) => (
              <ChatMessage
                key={m.id}
                message={m}
                operator={operator}
                labels={labels}
              />
            ))}
            {isTyping && <TypingIndicator label={typingLabel} />}
          </>
        )}
      </div>

      {aboveComposer}

      {notice ? (
        <div className="wc-system" style={{ padding: '12px 24px' }}>
          {notice}
        </div>
      ) : (
        <ChatComposer
          placeholder={labels.placeholder}
          sendLabel={labels.send}
          disabled={composerDisabled}
          autoFocus
          onSend={onSend}
        />
      )}
    </div>
  )
}
